import {
  PizzaType,
} from "@/api/custom-schema";
import { 
  PizzaItem,
  PostEvaluatePizzaAdvanced200,
  EvaluationMetadata,
  EvaluationLayer
} from "@/api/pizza-checker/schemas";
import { useGetAllPizzaTypes } from "@/api/pizza-checker/pizza-types/pizza-types";
import {
  useGetPizzaById,
  usePostEvaluatePizzaAdvanced,
} from "@/api/pizza-checker/pizzas/pizzas";
import { defaultHeight, defaultWidth } from "@/constants/image";
import { useAsset, useAssetImage } from "@/hooks/use-asset";
import {
  decodeSauceMask,
  SauceMask,
} from "@/lib/hovered-component-overlay";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/** Doneness gauge being hovered; each has its own heatmap layer (two-output models). */
export type HoveredHeatmap = "burnt" | "raw";
const HEATMAP_LAYERS: Record<HoveredHeatmap, string> = {
  burnt: "heatmap_burnt",
  raw: "heatmap_uncooked",
};

interface PizzaEvaluationContextType {
  pizza: PizzaItem | undefined;
  pizzaImageUrl: string | null;
  pizzaImage: HTMLImageElement | null;
  pizzaType: PizzaType | null;
  pizzaTypes: PizzaType[] | undefined;
  evaluationData: PostEvaluatePizzaAdvanced200 | undefined;
  evaluatePizza: (reevaluate: boolean) => void;
  layers: EvaluationLayer[] | undefined;
  metadata: EvaluationMetadata | null | undefined;
  selectedPizzaId: string | null;
  setSelectedPizzaId: (pizzaId: string | null) => void;
  loading: boolean;
  enabledLayers: Record<string, boolean>;
  setEnabledLayers: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  layerOpacity: Record<string, number>;
  setLayerOpacity: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  hoveredClass: string | null;
  setHoveredClass: React.Dispatch<React.SetStateAction<string | null>>;
  /** Decoded sauce_mask asset (class id per ROI pixel); null until loaded / when absent. */
  sauceMask: SauceMask | null;
  /** Doneness gauge under the mouse; its per-output heatmap is shown over the photo. */
  hoveredHeatmap: HoveredHeatmap | null;
  setHoveredHeatmap: React.Dispatch<React.SetStateAction<HoveredHeatmap | null>>;
  /** Decoded heatmap_burnt / heatmap_uncooked layer of the hovered gauge (null when none / not loaded). */
  hoveredHeatmapImage: HTMLImageElement | null;
  hoveredQuartal: string | null;
  setHoveredQuartal: React.Dispatch<React.SetStateAction<string | null>>;
  roi: { x: number; y: number; width: number; height: number };
  paddedPizzaRoi: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  /** false = the views show the whole frame instead of the pizza ROI */
  applyRoi: boolean;
  setApplyRoi: React.Dispatch<React.SetStateAction<boolean>>;
  setRoi: React.Dispatch<
    React.SetStateAction<{
      x: number;
      y: number;
      width: number;
      height: number;
    }>
  >;
  layerZIndex: Record<string, number>;
  setLayerZIndex: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  layerBlendMode: Record<string, string>;
  setLayerBlendMode: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
}

interface PizzaEvaluationProviderProps {
  children: React.ReactNode;
}

const PizzaEvaluationContext = createContext<
  PizzaEvaluationContextType | undefined
>(undefined);

export const PizzaEvaluationProvider = ({
  children,
}: PizzaEvaluationProviderProps) => {
  const { data: pizzaTypes } = useGetAllPizzaTypes<PizzaType[]>({
    query: {
      refetchInterval: 60 * 60 * 1000,
      queryKey: ["pizzaTypes"],
    },
  });

  const [selectedPizzaId, setSelectedPizzaId] = useState<string | null>(null);
  const { data } = useGetPizzaById(selectedPizzaId as string, {
    query: {
      enabled: selectedPizzaId != null,
    },
  });

  const pizza = data?.pizza;

  const { src: pizzaImageUrl } = useAsset({
    pizzaId: selectedPizzaId as string,
    enabled: selectedPizzaId != null,
    scale: 100,
  });

  // const { data: pizzaImage } = useGetGetAsset<Blob>(
  //   {
  //     pizza_id: selectedPizzaId || "",
  //     type: "source",
  //   },
  //   {
  //     request: {
  //       responseType: "blob",
  //     },
  //     query: {
  //       retry: 0,
  //       enabled: selectedPizzaId != null,
  //       queryKey: [selectedPizzaId],
  //       staleTime: 10 * 60 * 1000,
  //     },
  //   },
  // );

  const {
    mutateAsync: evaluate,
    data: evaluationData,
    // refetch: evaluate,
    isPending: isLoading,
  } = usePostEvaluatePizzaAdvanced();

  // const evaluationData = useMemo(() => {
  //   if (data) {
  //     return data as PizzaEvaluationData;
  //   }
  //   return undefined;
  // }, [data]);

  const queryClient = useQueryClient();
  const evaluatePizza = useCallback(
    (reevaluate: boolean = false) => {
      if (selectedPizzaId) {
        const request = evaluate({
          data: {
            pizza_id: selectedPizzaId,
            reevaluate,
          },
        });
        if (reevaluate) {
          // A re-evaluation rewrites every layer asset in storage; the asset queries
          // are keyed [pizzaId, type, scale] with a 10 min staleTime, so drop them or
          // the canvases keep showing the previous evaluation's images.
          request
            .then(() =>
              queryClient.invalidateQueries({ queryKey: [selectedPizzaId] }),
            )
            .catch(() => undefined);
        }
      }
    },
    [evaluate, queryClient, selectedPizzaId],
  );

  useEffect(() => {
    if (!selectedPizzaId) return;
    evaluatePizza();
  }, [evaluatePizza, selectedPizzaId]);

  // raw model responses (uint8, per output) back the single doneness heatmap; not shown as
  // layers. sauce_mask is the ROI-sized classmap: shown by hovering a sauce chip, not as a layer.
  const layerBlackList = ["cropped", "heatmap_raw", "heatmap_uncooked_raw", "sauce_mask"];
  const layers = useMemo(() => {
    const filteredLayers = evaluationData?.layers?.filter(
      (layer) => !layerBlackList.includes(layer.name),
    );
    const layers = [
      {
        name: "source",
        shape: [3040, 4056],
        type: "source",
        url: "",
      },
      ...(filteredLayers || []),
    ];
    return layers;
  }, [evaluationData?.layers]);

  const pizzaType = useMemo(() => {
    if (!pizza?.name) return null;
    const match = pizza.name.match(/^[^0-9]*\s?(\d+).\s.*/);
    if (match) {
      const pizzaType = match[1];
      return pizzaTypes?.find((type) => type.id === pizzaType) || null;
    }
    return null;
  }, [pizza, pizzaTypes]);

  const metadata = evaluationData?.metadata;

  // --- Layer state moved here ---
  const [enabledLayers, setEnabledLayers] = useState<Record<string, boolean>>(
    {},
  );
  const [layerOpacity, setLayerOpacity] = useState<Record<string, number>>({});
  const [hoveredClass, setHoveredClass] = useState<string | null>(null);

  // the sauce classmap behind the sauce chips' hover overlay
  const hasSauceMask = !!evaluationData?.layers?.some(
    (layer) => layer.name === "sauce_mask",
  );
  const { src: sauceMaskSrc } = useAsset({
    pizzaId: selectedPizzaId as string,
    type: "sauce_mask",
    enabled: !!selectedPizzaId && hasSauceMask,
  });
  const [sauceMask, setSauceMask] = useState<SauceMask | null>(null);
  useEffect(() => {
    if (!sauceMaskSrc) {
      setSauceMask(null);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setSauceMask(decodeSauceMask(img));
    };
    img.src = sauceMaskSrc;
    return () => {
      cancelled = true;
    };
  }, [sauceMaskSrc]);

  // per-output doneness heatmaps behind the gauge hover (two-output models only)
  const [hoveredHeatmap, setHoveredHeatmap] = useState<HoveredHeatmap | null>(null);
  const hasLayer = (name: string) =>
    !!evaluationData?.layers?.some((layer) => layer.name === name);
  const burntHeatmapImage = useAssetImage({
    pizzaId: selectedPizzaId as string,
    type: HEATMAP_LAYERS.burnt,
    enabled: !!selectedPizzaId && hasLayer(HEATMAP_LAYERS.burnt),
  });
  const rawHeatmapImage = useAssetImage({
    pizzaId: selectedPizzaId as string,
    type: HEATMAP_LAYERS.raw,
    enabled: !!selectedPizzaId && hasLayer(HEATMAP_LAYERS.raw),
  });
  const hoveredHeatmapImage =
    hoveredHeatmap === "burnt"
      ? burntHeatmapImage
      : hoveredHeatmap === "raw"
        ? rawHeatmapImage
        : null;
  const [hoveredQuartal, setHoveredQuartal] = useState<string | null>(null);

  // Store previous enabledLayers for quartal hover effect
  // const [prevEnabledLayers, setPrevEnabledLayers] = useState<Record<
  //   string,
  //   boolean
  // > | null>(null);

  // useEffect(() => {
  //   console.log(`Enabled layers: ${JSON.stringify(enabledLayers)}`);
  //   if (hoveredQuartal) {
  //     // Save current enabledLayers only once when hover starts
  //     if (!prevEnabledLayers) {
  //       setPrevEnabledLayers(enabledLayers);
  //     }
  //     // Enable the quartal mask layer
  //     const maskLayer = `quartal_mask_${hoveredQuartal}`;
  //     setEnabledLayers((layers) => ({
  //       ...layers,
  //       [maskLayer]: true,
  //     }));
  //   } else if (prevEnabledLayers) {
  //     // Restore previous enabledLayers when hover ends
  //     setEnabledLayers(prevEnabledLayers);
  //     setPrevEnabledLayers(null);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [hoveredQuartal]);

  // ROI and z-index state
  const [roi, setRoi] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  }>({
    x: 0,
    y: 0,
    width: 4056,
    height: 3040,
  });
  const [layerZIndex, setLayerZIndex] = useState<Record<string, number>>({});
  const [layerBlendMode, setLayerBlendMode] = useState<Record<string, string>>(
    {},
  );

  // Restore from localStorage or set defaults
  useEffect(() => {
    const cachedEnabledLayers = localStorage.getItem("enabledLayers");
    const cachedLayerOpacity = localStorage.getItem("layerOpacity");
    const cachedLayerBlendMode = localStorage.getItem("layerBlendMode");

    if (cachedEnabledLayers) {
      setEnabledLayers(JSON.parse(cachedEnabledLayers));
    } else {
      setEnabledLayers({
        source: true,
        heatmap: true,
        pizza_mask: true,
      });
    }

    if (cachedLayerOpacity) {
      setLayerOpacity(JSON.parse(cachedLayerOpacity));
    } else {
      setLayerOpacity({
        source: 100,
        heatmap: 80,
        pizza_mask: 80,
      });
    }

    if (cachedLayerBlendMode) {
      setLayerBlendMode(JSON.parse(cachedLayerBlendMode));
    } else if (evaluationData?.layers) {
      const initialBlendMode = evaluationData.layers.reduce(
        (acc, layer) => {
          acc[layer.name] = "normal";
          return acc;
        },
        {} as Record<string, string>,
      );
      setLayerBlendMode(initialBlendMode);
    }
  }, [evaluationData?.layers]);

  useEffect(() => {
    if (Object.keys(enabledLayers).length === 0) return;
    localStorage.setItem("enabledLayers", JSON.stringify(enabledLayers));
  }, [enabledLayers]);

  useEffect(() => {
    if (Object.keys(layerOpacity).length === 0) return;
    localStorage.setItem("layerOpacity", JSON.stringify(layerOpacity));
  }, [layerOpacity]);

  useEffect(() => {
    if (Object.keys(layerBlendMode).length === 0) return;
    localStorage.setItem("layerBlendMode", JSON.stringify(layerBlendMode));
  }, [layerBlendMode]);

  const [applyRoi, setApplyRoi] = useState(true);
  const [pizzaImage, setPizzaImage] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!pizzaImageUrl) {
      setPizzaImage(null);
      return;
    }
    const img = new Image();
    img.src = pizzaImageUrl;
    img.onload = () => setPizzaImage(img);
  }, [pizzaImageUrl]);

  // const pizzaImage = useMemo(() => {
  //   if (!pizzaImageUrl) return null;
  //   const img = new Image();
  //   img.src = pizzaImageUrl;
  //   return img;
  // }, [pizzaImageUrl]);

  const paddedPizzaRoi: {
    x: number;
    y: number;
    width: number;
    height: number;
  } = useMemo(() => {
    const img = pizzaImage;

    if (!applyRoi || !metadata || !metadata.pizza_roi) {
      return {
        x: 0,
        y: 0,
        width: img?.width || defaultWidth,
        height: img?.height || defaultHeight,
      };
    }

    const { pizza_roi } = metadata;
    const padding = 0.02; // 10% padding
    return {
      x: pizza_roi[0] - pizza_roi[2] * padding,
      y: pizza_roi[1] - pizza_roi[3] * padding,
      width: pizza_roi[2] + pizza_roi[2] * padding * 2,
      height: pizza_roi[3] + pizza_roi[3] * padding * 2,
    };
  }, [metadata, pizzaImage, applyRoi]);

  return (
    <PizzaEvaluationContext.Provider
      value={useMemo(
        () => ({
          pizza,
          pizzaImage,
          pizzaImageUrl,
          pizzaType,
          pizzaTypes,
          evaluationData,
          layers,
          evaluatePizza,
          metadata,
          selectedPizzaId,
          setSelectedPizzaId,
          loading: isLoading,
          enabledLayers,
          setEnabledLayers,
          layerOpacity,
          setLayerOpacity,
          hoveredClass,
          setHoveredClass,
          sauceMask,
          hoveredHeatmap,
          setHoveredHeatmap,
          hoveredHeatmapImage,
          hoveredQuartal,
          setHoveredQuartal,
          roi,
          paddedPizzaRoi,
          setRoi,
          applyRoi,
          setApplyRoi,
          layerZIndex,
          setLayerZIndex,
          layerBlendMode,
          setLayerBlendMode,
        }),
        [
          pizza,
          pizzaImage,
          pizzaImageUrl,
          pizzaType,
          pizzaTypes,
          evaluationData,
          layers,
          evaluatePizza,
          metadata,
          selectedPizzaId,
          isLoading,
          enabledLayers,
          layerOpacity,
          hoveredClass,
          sauceMask,
          hoveredHeatmap,
          hoveredHeatmapImage,
          hoveredQuartal,
          roi,
          paddedPizzaRoi,
          applyRoi,
          layerZIndex,
          layerBlendMode,
        ],
      )}
    >
      {children}
    </PizzaEvaluationContext.Provider>
  );
};

export const usePizzaEvaluationContext = () => {
  const context = useContext(PizzaEvaluationContext);
  if (!context) {
    throw new Error(
      "usePizzaEvaluationContext must be used within a PizzaEvaluationProvider",
    );
  }
  return context;
};
